import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, Grid, Skeleton, Tooltip, Typography, useTheme } from "@mui/material";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import { useNavigate } from "react-router-dom";
import { AdminPageHeader, ChoiceChips, EmptyState, StatCard } from "../../../components/ui";
import { useAuth } from "../../../contexts/auth2";
import { getActivesPaginated, getSubclassPaginated } from "../../../services/decisionFlow";
import { getAllOralActive } from "../../../services/oralService";
import { getProductsWithFilterNPagination } from "../../../services/productService";
import { getVehicles } from "../../../services/vehicleService";
import { getUserProfile } from "../../../services/profileService";
import { PaginatedResponse } from "../../../domain/models/paginated-response.model";
import { ProductsWithFilterNPaginationResponse } from "../../../services/productService";
import {
  AdminOverview,
  getAdminOverview,
  getAdminPending,
  getAdminRecentActivity,
  getAdminSignups,
  PendingItem,
  RecentActivity,
  SignupWeek,
} from "../../../services/admin-stats.service";

// ----------------------------------------------------------------------

type PeriodOption = "hoje" | "7d" | "30d";

const PERIOD_OPTIONS: Array<{ value: PeriodOption; label: string }> = [
  { value: "hoje", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
];

const periodQuery = (p: PeriodOption) => (p === "hoje" ? "1d" : p);

interface CatalogRow {
  label: string;
  count: number | null;
}

const formatCurrency = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const deltaChip = (pct: number | null): string | undefined =>
  pct != null && pct > 0 ? `↑ ${pct}%` : undefined;

const shortDate = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

async function safe<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------

export const AdminOverviewPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [period, setPeriod] = useState<PeriodOption>("30d");
  const [profileName, setProfileName] = useState("");

  const displayName = profileName || (user.email ?? "").split("@")[0];
  const firstName = displayName.trim().split(/\s+/)[0] || "";
  const greetingTitle = `Olá, ${firstName}`;

  const [catalogRows, setCatalogRows] = useState<CatalogRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogTotal, setCatalogTotal] = useState<number | null>(null);

  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [signups, setSignups] = useState<SignupWeek[] | null>(null);
  const [activity, setActivity] = useState<RecentActivity[] | null>(null);
  const [pending, setPending] = useState<PendingItem[] | null>(null);

  useEffect(() => {
    if (!user.idUser) return;
    let cancelled = false;
    (async () => {
      const res = await safe(getUserProfile(user.idUser));
      if (!cancelled && res) setProfileName(res.data?.name_User ?? "");
    })();
    return () => { cancelled = true; };
  }, [user.idUser]);

  // Catálogo (dados reais, endpoints de listagem)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [activesRes, subclassRes, oralRes, productsRes, vehiclesRes] = await Promise.all([
        safe(getActivesPaginated(1, 1)),
        safe(getSubclassPaginated(1, 1)),
        safe(getAllOralActive(true)),
        safe(getProductsWithFilterNPagination(0, 1, {} as any)),
        safe(getVehicles()),
      ]);
      if (cancelled) return;

      const activesTotal = activesRes ? (activesRes.data as PaginatedResponse<any>).pagination?.total ?? null : null;
      const subclassTotal = subclassRes ? (subclassRes.data as PaginatedResponse<any>).pagination?.total ?? null : null;
      const oralTotal = oralRes ? (Array.isArray(oralRes.data) ? (oralRes.data as any[]).length : null) : null;
      const productsTotal = productsRes
        ? Array.isArray(productsRes.data) && (productsRes.data as ProductsWithFilterNPaginationResponse[]).length > 0
          ? (productsRes.data as ProductsWithFilterNPaginationResponse[])[0].totalProducts ?? null
          : null
        : null;
      const vehiclesTotal = vehiclesRes ? (Array.isArray(vehiclesRes.data) ? (vehiclesRes.data as any[]).length : null) : null;

      const rows: CatalogRow[] = [
        { label: "Ativos / Classes", count: activesTotal },
        { label: "Ativos orais", count: oralTotal },
        { label: "Produtos", count: productsTotal },
        { label: "Veículos", count: vehiclesTotal },
        { label: "Subclasses", count: subclassTotal },
      ];
      const sum = rows.reduce<number | null>((acc, r) => (r.count === null ? acc : (acc ?? 0) + r.count), null);

      setCatalogRows(rows);
      setCatalogTotal(sum);
      setCatalogLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // KPIs/overview — refaz ao trocar o período
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    const res = await safe(getAdminOverview(periodQuery(period)));
    setOverview(res ? res.data : null);
    setOverviewLoading(false);
  }, [period]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Cadastros — refaz ao trocar o período
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await safe(getAdminSignups(periodQuery(period)));
      if (!cancelled) setSignups(s ? s.data : null);
    })();
    return () => { cancelled = true; };
  }, [period]);

  // Atividade / pendências (uma vez)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [a, p] = await Promise.all([
        safe(getAdminRecentActivity(8)),
        safe(getAdminPending()),
      ]);
      if (cancelled) return;
      setActivity(a ? a.data : null);
      setPending(p ? p.data : null);
    })();
    return () => { cancelled = true; };
  }, []);

  const maxCatalog = catalogRows.reduce((max, r) => (r.count !== null && r.count > max ? r.count : max), 1);
  const maxSignup = Math.max(1, ...(signups ?? []).map((s) => s.count));
  const goldGradient = `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`;

  const cardSx = {
    backgroundColor: "background.paper",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "16px",
    p: "22px",
  } as const;

  const sectionTitleSx = {
    fontFamily: theme.typography.fontFamilyDisplay,
    fontSize: 17,
    color: "text.primary",
    mb: 1.5,
  } as const;

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 4 } }}>
      <AdminPageHeader
        title={greetingTitle}
        subtitle="Visão geral do backoffice"
        action={<ChoiceChips options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />}
      />

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<InventoryOutlinedIcon />}
            value={catalogLoading ? <Skeleton width={60} /> : (catalogTotal !== null ? catalogTotal.toLocaleString("pt-BR") : "—")}
            label="Itens no catálogo"
            sublabel={catalogLoading || catalogTotal === null ? undefined : "total no sistema"}
            delta={overview && overview.catalogItems.deltaCount > 0 ? `↑ ${overview.catalogItems.deltaCount}` : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<PeopleOutlinedIcon />}
            value={overviewLoading ? <Skeleton width={60} /> : (overview ? overview.activeUsers.value.toLocaleString("pt-BR") : "—")}
            label="Usuários ativos"
            sublabel={!overview && !overviewLoading ? "Indisponível" : "com plano vigente"}
            delta={overview ? deltaChip(overview.activeUsers.deltaPercent) : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<SubscriptionsOutlinedIcon />}
            value={overviewLoading ? <Skeleton width={60} /> : (overview ? overview.newPlans.value.toLocaleString("pt-BR") : "—")}
            label="Novos planos"
            sublabel={!overview && !overviewLoading ? "Indisponível" : period === "hoje" ? "hoje" : period === "7d" ? "últimos 7 dias" : "últimos 30 dias"}
            delta={overview ? deltaChip(overview.newPlans.deltaPercent) : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<AttachMoneyOutlinedIcon />}
            value={overviewLoading ? <Skeleton width={70} /> : (overview ? formatCurrency(overview.revenue.value) : "—")}
            label="Receita de planos"
            sublabel={!overview && !overviewLoading ? "Indisponível" : period === "hoje" ? "hoje" : period === "7d" ? "últimos 7 dias" : "últimos 30 dias"}
            delta={overview ? deltaChip(overview.revenue.deltaPercent) : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Catálogo (real) */}
        <Grid item xs={12} md={7}>
          <Box sx={cardSx}>
            <Typography sx={{ ...sectionTitleSx, fontSize: 21 }}>Catálogo</Typography>
            {catalogLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton width="60%" height={14} />
                  <Skeleton width="100%" height={8} sx={{ mt: 0.5, borderRadius: "100px" }} />
                </Box>
              ))
            ) : (
              catalogRows.map((row) => {
                const pct = row.count !== null ? Math.round((row.count / maxCatalog) * 100) : 0;
                return (
                  <Box key={row.label} sx={{ mb: 1.75 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>{row.label}</Typography>
                      <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "text.primary" }}>
                        {row.count !== null ? row.count.toLocaleString("pt-BR") : "—"}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: "100px", backgroundColor: theme.palette.brand.cream, overflow: "hidden" }}>
                      {row.count !== null && (
                        <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: "100px", background: goldGradient, transition: "width .4s ease" }} />
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Grid>

        {/* Novos cadastros */}
        <Grid item xs={12} md={5}>
          <Box sx={{ ...cardSx, height: "100%" }}>
            <Typography sx={sectionTitleSx}>Novos cadastros</Typography>
            {signups === null ? (
              <EmptyState title="Indisponível" description="Não foi possível carregar os cadastros." />
            ) : signups.length === 0 ? (
              <EmptyState title="Sem cadastros" description="Nenhum cadastro no período." />
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: signups.length > 10 ? 0.4 : 1, height: 120, pt: 1 }}>
                {signups.map((s, idx) => {
                  const labelStep = Math.max(1, Math.ceil(signups.length / 8));
                  const showLabel = idx % labelStep === 0 || idx === signups.length - 1;
                  return (
                    <Box key={s.weekStart} sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: 90 }}>
                        <Tooltip title={`${shortDate(s.weekStart)} · ${s.count} cadastro(s)`} placement="top" arrow>
                          <Box
                            sx={{ width: "100%", height: `${Math.round((s.count / maxSignup) * 100)}%`, minHeight: s.count > 0 ? 4 : 0, borderRadius: "4px 4px 0 0", background: goldGradient, cursor: "default" }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography sx={{ fontSize: "9.5px", color: "text.secondary", whiteSpace: "nowrap" }}>{showLabel ? shortDate(s.weekStart) : " "}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Atividade recente — full width, itens em 2 colunas para não espremer */}
      <Box sx={{ ...cardSx, mt: 2 }}>
        <Typography sx={sectionTitleSx}>Atividade recente</Typography>
        {activity === null ? (
          <EmptyState title="Indisponível" description="Não foi possível carregar a atividade." />
        ) : activity.length === 0 ? (
          <EmptyState title="Sem atividade" description="Nada recente por aqui." />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 3, rowGap: 1.5 }}>
            {activity.map((a) => (
              <Box key={a.id} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: "10px", backgroundColor: theme.palette.brand.hexTileBg, color: "primary.main", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
                  {(a.actorName || a.title || "?").charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary", minWidth: 0, flexShrink: 1 }} noWrap>{a.title}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "text.secondary", flexShrink: 0 }}>{shortDate(a.at)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "text.secondary" }} noWrap>{a.description}</Typography>
                </Box>
                {a.tag && <Chip label={a.tag} size="small" sx={{ backgroundColor: theme.palette.brand.cream, color: "primary.dark", fontWeight: 600, height: 20 }} />}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Pendências */}
      <Box sx={{ ...cardSx, mt: 2 }}>
        <Typography sx={sectionTitleSx}>Pendências</Typography>
        {pending === null ? (
          <EmptyState title="Indisponível" description="Não foi possível carregar as pendências." />
        ) : pending.length === 0 ? (
          <EmptyState title="Tudo em dia" description="Nenhuma pendência no momento." />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {pending.map((p) => (
              <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: p.tone === "error" ? "error.main" : p.tone === "warning" ? "warning.main" : "primary.main" }} />
                  <Typography sx={{ fontSize: "13px", color: "text.primary" }} noWrap>{p.label}</Typography>
                </Box>
                {p.actionLabel && (
                  <Button size="small" onClick={() => p.actionPath && navigate(p.actionPath)} sx={{ flexShrink: 0, color: "primary.main", fontWeight: 600 }}>
                    {p.actionLabel}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
